//import "bitboss-ui/styles.css"
import "@/assets/css/index.css";
import "@/assets/sass/index.scss?raw";
import "@/validation/index";
import { createApp } from "vue";
import { debounce } from "lodash";
// import { i18n, loadLocaleMessages } from "./i18n/locales";
import { setupI18n } from "./plugins/i18n";
import { instance } from "./api";
import { keycloak } from "@/utilities/keycloak";
import { useSettings } from "./store/settings";
import { useUI } from "./store/ui";
import { useUser } from "./store/user";
import { useAccount } from "./store/account";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import { LocalStorage } from "./storage";
import vuetify from './plugins/vuetify.ts';
import 'vuetify/styles';
import {
  // Directives
  VTooltip,
  VClosePopper,
  // Components
  Dropdown,
  Tooltip,
} from "floating-vue";
import "floating-vue/dist/style.css";
import { waitFor } from "./utilities/functions/waitFor";
import { useConfirm } from "./composables/useConfirm";
import "@mdi/font/css/materialdesignicons.css";
import { wait } from "./utilities/functions/wait";

const INIT_TIMEOUT_MS = 12000;

function withTimeout(promise, ms, onTimeout = () => { }) {
  return Promise.race([
    promise,
    wait(ms).then(() => {
      onTimeout();
      return undefined;
    }),
  ]).catch(() => undefined);
}
import iconPlugin from "./plugins/iconPlugin";

const app = createApp(App);
setupI18n(app);
app.use(store);
app.use(iconPlugin);
const settingsStore = useSettings();
let shouldMountApp = true;
keycloak
  /**
   * If the user logged in through keycloak revive that login
   */
  .init({
    onLoad: "check-sso",
    checkLoginIframe: false,
    enableLogging: false,
  })
  /**
   * If keycloak server is offline go on
   */
  .catch((error) => {
    console.error("Could not establish a connection to login server. Exception: ", error);
    return false;
  })
  .then(async (auth) => {
    let authenticated = auth;
    if (!authenticated && LocalStorage.getOr(["auth", "token"], false)) {
      keycloak.token = LocalStorage.getOr(["auth", "token"], undefined);
      keycloak.refreshToken = LocalStorage.getOr(
        ["auth", "refreshToken"],
        undefined
      );
      keycloak.idToken = LocalStorage.getOr(["auth", "idToken"], undefined);
      try {
        await keycloak.updateToken(10);
        authenticated = keycloak.authenticated;
      } catch (error) {
        LocalStorage.removeKey(["auth", "token"]);
        LocalStorage.removeKey(["auth", "refreshToken"]);
        authenticated = false;
      }
    }
    // Initialize the current breakpoint
    const UI = useUI();
    UI.getBreakpoint();
    // Auto update on resize
    const onResize = debounce(UI.getBreakpoint, 500);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    instance.defaults.headers["X-Application"] = settingsStore.platformIdHeader;
    instance.defaults.headers["X-FrontendId"] = settingsStore.platformFrontendId;

    const accountStore = useAccount();
    const contextApplicationId =
      new URLSearchParams(window.location.search).get("context-application-id") ?? undefined;

    await withTimeout(accountStore.init(contextApplicationId), INIT_TIMEOUT_MS, () =>
      console.warn("[Account] init timeout")
    );

    const accountApplicationId =
      contextApplicationId ||
      (import.meta.env.VITE_ACCOUNT_APPLICATION_ID || "").trim() ||
      null;
    if (accountApplicationId) {
      await withTimeout(
        accountStore.fetchAccountApplication(accountApplicationId),
        INIT_TIMEOUT_MS,
        () => console.warn("[Account] fetchAccountApplication timeout")
      );
    } else {
      accountStore.setAccountApplicationFromInitResponse();
    }

    if (accountStore.isExternalPlatformOrigin) {
      //the call comes from another application, we need to change the application header for all the next api calls
      instance.defaults.headers["X-Application"] = accountStore.current.code;
    }

    // for TEST remove comment at line of code below to impersonate a specific Application
    // instance.defaults.headers["X-Application"] = "AGORA";

    const userStore = useUser();

    if (authenticated) {
      // After a post-registration keycloak.login() in ThankYou.vue the user
      // returns here with a real browser session. Forward to the platform app
      // before mounting — it will find the Keycloak session cookie via prompt=none.
      const pendingRedirect = sessionStorage.getItem("account:post-auth-redirect");
      if (pendingRedirect) {
        sessionStorage.removeItem("account:post-auth-redirect");
        shouldMountApp = false;
        // Persist the fresh auth-code tokens so the ROPC tokens from registration
        // are evicted from localStorage. These tokens are tied to the SSO session:
        // when the user later logs out (from any HD app) the SSO session is
        // invalidated, updateToken() fails, and the user is correctly seen as
        // unauthenticated on the next visit to the registration page.
        userStore.setTokens(keycloak);
        window.location.href = pendingRedirect;
        return;
      }

      try {
        userStore.setTokens(keycloak);
        // Get details of current user
        await Promise.all([userStore.getCurrent(), userStore.getRoles()]);

        setInterval(async () => {
          try {
            // Refresh token when remaining lifespan is less than 60 seconds
            const refreshed = await keycloak.updateToken(60);
            // Token was refresh as it's lifespan was less than 60 seconds
            if (refreshed) {
              userStore.setTokens(keycloak);
              // Token needed not be refreshed as it is still valid
            } else {
              // Remaining lifespan
              /*    console.log('Token not refreshed, valid for '
        + Math.round(keycloak.tokenParsed.exp + keycloak.timeSkew - new Date().getTime() / 1000) + ' seconds'); */
            }
          } catch (error) {
            console.error("Failed to refresh token: ", error, "\nDetail:", JSON.stringify(keycloak));
          }
        }, 60000);
      } catch (error) {
        console.error(error);
        /*
        When backend services are offline and info about the current user could not be retrieved tell the user
        */
        waitFor(() => document.querySelector(".base-confirm")).then(
          async () => {
            const { confirm, close } = useConfirm();
            await Promise.race([
              confirm({
                title: "Errore",
                text: `Non è stato possible caricare i dati relativi all'utente, per favore riprova più tardi`,
                no: false,
                yesText: "OK",
              }),
              wait(6000),
            ]);
            close();
          }
        );
      }

      instance.interceptors.response.use((data) => data, async (error) => {
        //if (error.response.status == 403) {
        //  const refreshed = await keycloak.updateToken(60);
        //  if (!refreshed) {
        //    const { confirm, close } = useConfirm();
        //    await Promise.race([
        //      confirm({
        //        title: "Errore",
        //        text: `La tua sessione non è più valida. Effettua il login nuovamente`,
        //        no: false,
        //        yesText: "OK",
        //      }),
        //     wait(5000),
        //    ]);
        //    close();
        //    userStore.logout();
        //  }
        //}
        return Promise.reject(error);
      });

      // If we start the app already authenticated and we're on a public/callback route,
      // send the user straight to My Services as default landing.
      try {
        const basePath = "/account";
        const fullPath = window.location.pathname || "/";
        const relativePath = fullPath.startsWith(basePath)
          ? fullPath.slice(basePath.length) || "/"
          : fullPath;
        const callbackPaths = ["/", "/home", "/public-home", "/login"];
        if (callbackPaths.includes(relativePath)) {
          await router.replace({ name: "my-services" });
        }
      } catch (e) {
        console.warn("[Account] Initial redirect to my-services failed", e);
      }

    }
    settingsStore.authInitialized = true;
  })
  //.then(() => loadLocaleMessages(i18n, "it"))
  .then(() => {
    if (!shouldMountApp) return;
    app.use(router);
    app.use(vuetify);
    app.directive("tooltip", VTooltip);
    app.directive("close-popper", VClosePopper);

    app.component("VDropdown", Dropdown);
    app.component("VTooltip", Tooltip);
    app.mount("#app");
  });